
import React, { useState, useEffect, useRef } from "react";
import styles from "./salesvscogs.module.css";
import { salesCogsApi } from "../../../api/home/salescogsapi";
import SalesCogsFilters from "./SalesCogsFilters/SalesCogsFilters";
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement,
    LineElement, PointElement, Title, Tooltip, Legend,
    BarController, LineController
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale, LinearScale, BarElement, LineElement, PointElement,
    Title, Tooltip, Legend, BarController, LineController
);

// Financial year helpers
const getCurrentFY = () => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    const startYear = month >= 4 ? year : year - 1;
    return `${startYear}-${String(startYear + 1).slice(2)}`;
};

const buildFYOptions = () => {
    const current = new Date().getFullYear();
    const options = [];
    for (let y = current; y >= 2022; y--) {
        options.push(`${y}-${String(y + 1).slice(2)}`);
    }
    return options;
};

const SalesVsCogs = () => {
    const chartRef = useRef(null);
    const tableContainerRef = useRef(null);

    // ── State ──────────────────────────────────────────────────
    const [financialYear, setFinancialYear] = useState(getCurrentFY());
    const [activeFilters, setActiveFilters] = useState({});
    const [chartData, setChartData] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fyOptions = buildFYOptions();

    // ── Fetch ──────────────────────────────────────────────────
    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Build filters — handle comma-separated multi-values
            // (Backend currently supports single values; take first for now)
            const filtersToSend = {};
            Object.entries(activeFilters).forEach(([key, val]) => {
                if (val) {
                    const first = val.split(',')[0];
                    if (first) filtersToSend[key] = first;
                }
            });

            const response = await salesCogsApi.getSalesCogsData(financialYear, filtersToSend);
            const data = response.data || response;
            const roleId = response.roleId;

            setUserRole(roleId);
            prepareChartData(data, roleId);
            setTableData(data);
        } catch (err) {
            setError('Failed to load data');
            console.error('❌ Error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [financialYear, activeFilters]);

    // Scroll table to right when data loads
    useEffect(() => {
        if (tableContainerRef.current && tableData.length > 0) {
            setTimeout(() => {
                tableContainerRef.current.scrollLeft = tableContainerRef.current.scrollWidth;
            }, 100);
        }
    }, [tableData]);

    // ── Chart preparation ──────────────────────────────────────
    const prepareChartData = (data, roleId) => {
        if (!data || data.length === 0) { setChartData(null); return; }

        const isContactPerson = roleId === 1;
        const labels = data.map(d => d.month);

        const datasets = [
            {
                label: 'Lines',
                data: data.map(d => d.lines),
                backgroundColor: '#06b6d4',
                type: 'bar',
                yAxisID: 'y2',
                order: 4
            },
            {
                label: 'Sales',
                data: data.map(d => d.sales),
                backgroundColor: '#1e40af',
                type: 'bar',
                yAxisID: 'y',
                order: 3
            },
            {
                label: 'Order Value',
                data: data.map(d => d.orderValue),
                backgroundColor: '#f97316',
                type: 'bar',
                yAxisID: 'y',
                order: 2
            }
        ];

        if (!isContactPerson) {
            datasets.push({
                label: 'COGS',
                data: data.map(d => d.cogs),
                backgroundColor: '#16a34a',
                type: 'bar',
                yAxisID: 'y',
                order: 1
            });
            datasets.push({
                label: 'GM%',
                data: data.map(d => d.grossMarginPct),
                borderColor: '#4ade80',
                backgroundColor: 'rgba(74,222,128,0.1)',
                borderWidth: 3,
                type: 'line',
                yAxisID: 'y1',
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: '#4ade80',
                pointBorderColor: '#ffffff',
                pointBorderWidth: 2,
                fill: true,
                order: 0
            });
        }

        setChartData({ labels, datasets });
    };

    const getChartOptions = () => {
        const isContactPerson = userRole === 1;
        const options = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { position: 'top', labels: { usePointStyle: true, padding: 15, font: { size: 12 } } },
                tooltip: {
                    callbacks: {
                        label: (ctx) => {
                            let label = ctx.dataset.label + ': ';
                            if (ctx.dataset.yAxisID === 'y1') return label + ctx.parsed.y.toFixed(2) + '%';
                            if (ctx.dataset.yAxisID === 'y2') return label + ctx.parsed.y.toLocaleString('en-IN');
                            return label + '₹' + ctx.parsed.y.toLocaleString('en-IN');
                        }
                    }
                }
            },
            scales: {
                x: { grid: { display: false }, ticks: { font: { size: 11 } } },
                y: {
                    type: 'linear', position: 'left',
                    title: { display: true, text: 'Amount (₹)', font: { size: 12, weight: 'bold' } },
                    ticks: { callback: (v) => '₹' + (v / 10000000).toFixed(1) + 'Cr', font: { size: 11 } },
                    grid: { color: 'rgba(0,0,0,0.05)' }
                },
                y2: {
                    type: 'linear', position: 'right',
                    title: { display: true, text: 'Lines', font: { size: 12, weight: 'bold' } },
                    min: 0, max: 6000,
                    ticks: { stepSize: 500, font: { size: 11 } },
                    grid: { drawOnChartArea: false }
                }
            }
        };

        if (!isContactPerson) {
            options.scales.y1 = {
                type: 'linear', position: 'right',
                title: { display: true, text: 'GM %', font: { size: 12, weight: 'bold' } },
                min: 0, max: 35,
                ticks: { callback: (v) => v + '%', stepSize: 5, font: { size: 11 } },
                grid: { drawOnChartArea: false }
            };
        }

        return options;
    };

    // ── Helpers ────────────────────────────────────────────────
    const fmt  = (n) => '₹' + (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 });
    const fmtP = (n) => (n || 0).toFixed(2) + '%';
    const fmtN = (n) => (n || 0).toLocaleString('en-IN');
    const total = (field) => tableData.reduce((s, d) => s + (Number(d[field]) || 0), 0);
    const totalGM = () => {
        const s = total('sales'), c = total('cogs');
        return s === 0 ? 0 : ((s - c) * 100) / s;
    };

    // ── Render ─────────────────────────────────────────────────
    return (
        <div className={styles.componentWrapper}>
            <div className={styles.headerSection}>
                <h2 className={styles.componentTitle}>Sales vs COGS</h2>
                <div className={styles.fyDropdown}>
                    <label>FY:</label>
                    <select
                        value={financialYear}
                        onChange={(e) => setFinancialYear(e.target.value)}
                        className={styles.select}
                    >
                        {fyOptions.map(fy => (
                            <option key={fy} value={fy}>{fy}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Filters — only for admin (3) and sales person (2) */}
            <SalesCogsFilters
                userRoleId={userRole}
                onFiltersChange={setActiveFilters}
            />

            {error && <div className={styles.errorMessage}>{error}</div>}

            {loading && (
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner} />
                    <span>Loading chart data...</span>
                </div>
            )}

            {!loading && chartData && (
                <>
                    <div className={styles.chartContainer}>
                        <Bar ref={chartRef} data={chartData} options={getChartOptions()} />
                    </div>

                    <div className={styles.tableContainer} ref={tableContainerRef}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.th}>Metric</th>
                                    {tableData.map((d, i) => <th key={i} className={styles.th}>{d.month}</th>)}
                                    <th className={styles.th}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className={styles.td}>Sales</td>
                                    {tableData.map((d, i) => <td key={i} className={styles.td}>{fmt(d.sales)}</td>)}
                                    <td className={styles.tdTotal}>{fmt(total('sales'))}</td>
                                </tr>

                                {userRole !== 1 && (
                                    <>
                                        <tr>
                                            <td className={styles.td}>COGS</td>
                                            {tableData.map((d, i) => <td key={i} className={styles.td}>{fmt(d.cogs)}</td>)}
                                            <td className={styles.tdTotal}>{fmt(total('cogs'))}</td>
                                        </tr>
                                        <tr>
                                            <td className={styles.td}>GM %</td>
                                            {tableData.map((d, i) => <td key={i} className={styles.td}>{fmtP(d.grossMarginPct)}</td>)}
                                            <td className={styles.tdTotal}>{fmtP(totalGM())}</td>
                                        </tr>
                                    </>
                                )}

                                <tr>
                                    <td className={styles.td}>Lines</td>
                                    {tableData.map((d, i) => <td key={i} className={styles.td}>{fmtN(d.lines)}</td>)}
                                    <td className={styles.tdTotal}>{fmtN(total('lines'))}</td>
                                </tr>
                                <tr>
                                    <td className={styles.td}>Order Value</td>
                                    {tableData.map((d, i) => <td key={i} className={styles.td}>{fmt(d.orderValue)}</td>)}
                                    <td className={styles.tdTotal}>{fmt(total('orderValue'))}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {!loading && !chartData && !error && (
                <div className={styles.loadingContainer}>
                    <p>No data available for the selected financial year.</p>
                </div>
            )}
        </div>
    );
};

export default SalesVsCogs;